"use client";

import * as React from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Send, PhoneCall, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function RequestEstimate() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    contact_method: "",
    timing: "",
    budget: "",
    details: "",
    consent: false
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) {
      setStatus("error");
      setErrorMessage("Please agree to the contact consent before submitting.");
      return;
    }
    if (!formData.project_type || !formData.contact_method || !formData.timing) {
      setStatus("error");
      setErrorMessage("Please select all required dropdown fields.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget || "Not specified");
      params.append("details", formData.details);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", currentPath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    }
  };
  return <section id="requestestimate" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Subtle Background Glows */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[42rem] h-[26rem] bg-secondary/60 rounded-full blur-3xl opacity-70" />
        <div className="absolute -bottom-24 right-10 w-96 h-96 bg-chart-1/10 rounded-full blur-2xl opacity-60" />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
            Request an Estimate
          </h2>
          <div className="flex items-center justify-center gap-3 my-4">
            <span className="h-px w-12 bg-chart-1/40" />
            <span className="h-1.5 w-1.5 rounded-full bg-chart-1" />
            <span className="h-px w-12 bg-chart-1/40" />
          </div>
          <p className="text-muted-foreground text-base sm:text-lg">
            Tell us about your home vision in Decatur. We will review your project requirements and schedule an on-site consultation.
          </p>
        </div>

        {/* Card Form */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }}>
          <Card className="border border-border/80 bg-card/85 backdrop-blur-md shadow-md rounded-xl p-6 sm:p-10">
            <CardContent className="p-0">
              <AnimatePresence mode="wait">
                {status === "success" ? <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} className="text-center py-10 px-4 space-y-5">
                    <div className="inline-flex items-center justify-center size-16 rounded-full bg-secondary text-chart-1 border border-border">
                      <CheckCircle2 className="size-9" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-serif text-2xl sm:text-3xl text-foreground font-medium">
                        Request Received
                      </h3>
                      <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base leading-relaxed">
                        Thank you for reaching out to Decatur Heritage Renovations. Our remodeling team has received your project details and will be in touch shortly via your preferred contact method.
                      </p>
                    </div>

                    <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Sparkles className="size-4 text-chart-1" />
                        <span>Prompt local review</span>
                      </div>
                      <span className="hidden sm:inline-block opacity-40">•</span>
                      <div className="flex items-center gap-2">
                        <PhoneCall className="size-4 text-chart-1" />
                        <span>Direct phone: (470) 847-9076</span>
                      </div>
                    </div>

                    <div className="pt-6">
                      <Button type="button" variant="outline" onClick={() => {
                    setStatus("idle");
                    setFormData({
                      name: "",
                      phone: "",
                      email: "",
                      address: "",
                      project_type: "",
                      contact_method: "",
                      timing: "",
                      budget: "",
                      details: "",
                      consent: false
                    });
                  }}>
                        Submit another inquiry
                      </Button>
                    </div>
                  </motion.div> : <form key="form" onSubmit={handleSubmit} className="space-y-6">
                    {/* Error Banner */}
                    {status === "error" && errorMessage && <Alert variant="destructive" className="border-destructive/30">
                        <AlertCircle className="size-4" />
                        <AlertTitle>Submission Error</AlertTitle>
                        <AlertDescription>{errorMessage}</AlertDescription>
                      </Alert>}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="estimate-name" className="text-foreground">
                          Full Name <span className="text-chart-1">*</span>
                        </Label>
                        <Input id="estimate-name" name="name" type="text" required value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Jane Doe" className="bg-background/60 focus-visible:ring-chart-1/30" />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="estimate-phone" className="text-foreground">
                          Phone Number <span className="text-chart-1">*</span>
                        </Label>
                        <Input id="estimate-phone" name="phone" type="tel" required value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="Your phone number" className="bg-background/60 focus-visible:ring-chart-1/30" />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="estimate-email" className="text-foreground">
                          Email Address <span className="text-chart-1">*</span>
                        </Label>
                        <Input id="estimate-email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => handleChange("email", e.target.value)} className="bg-background/60 focus-visible:ring-chart-1/30" />
                      </div>

                      {/* Address */}
                      <div className="space-y-2">
                        <Label htmlFor="estimate-address" className="text-foreground">
                          Street Address or ZIP Code <span className="text-chart-1">*</span>
                        </Label>
                        <Input id="estimate-address" name="address" type="text" required value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="Decatur, GA 30030" className="bg-background/60 focus-visible:ring-chart-1/30" />
                      </div>

                      {/* Project Type */}
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          Project Type <span className="text-chart-1">*</span>
                        </Label>
                        <Select value={formData.project_type} onValueChange={val => handleChange("project_type", val)} required>
                          <SelectTrigger className="w-full bg-background/60 focus-visible:ring-chart-1/30">
                            <SelectValue placeholder="Select a project category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                            <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                            <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                            <SelectItem value="Home Additions">Home Additions</SelectItem>
                            <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                            <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                            <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                            <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Preferred Contact Method */}
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          Preferred Contact Method <span className="text-chart-1">*</span>
                        </Label>
                        <Select value={formData.contact_method} onValueChange={val => handleChange("contact_method", val)} required>
                          <SelectTrigger className="w-full bg-background/60 focus-visible:ring-chart-1/30">
                            <SelectValue placeholder="How should we reach you?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Phone">Phone</SelectItem>
                            <SelectItem value="Text">Text</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Timing */}
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          Project Timing <span className="text-chart-1">*</span>
                        </Label>
                        <Select value={formData.timing} onValueChange={val => handleChange("timing", val)} required>
                          <SelectTrigger className="w-full bg-background/60 focus-visible:ring-chart-1/30">
                            <SelectValue placeholder="Desired start timeframe" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ASAP">ASAP</SelectItem>
                            <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                            <SelectItem value="1-3 months">1-3 months</SelectItem>
                            <SelectItem value="3-6 months">3-6 months</SelectItem>
                            <SelectItem value="Just researching">Just researching</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Budget */}
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          Estimated Budget <span className="text-muted-foreground text-xs">(Optional)</span>
                        </Label>
                        <Select value={formData.budget} onValueChange={val => handleChange("budget", val)}>
                          <SelectTrigger className="w-full bg-background/60 focus-visible:ring-chart-1/30">
                            <SelectValue placeholder="Approximate investment" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                            <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                            <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                            <SelectItem value="$100,000+">$100,000+</SelectItem>
                            <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-details" className="text-foreground">
                        Project Details <span className="text-muted-foreground text-xs">(Optional)</span>
                      </Label>
                      <Textarea id="estimate-details" name="details" rows={4} value={formData.details} onChange={e => handleChange("details", e.target.value)} placeholder="Tell us about the scope, specific rooms, layout changes, or design ideas you have in mind..." className="bg-background/60 focus-visible:ring-chart-1/30 resize-y" />
                    </div>

                    {/* Consent Checkbox */}
                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox id="estimate-consent" checked={formData.consent} onCheckedChange={checked => handleChange("consent", Boolean(checked))} required className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                      <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                        I agree to be contacted about my project by phone, text, or email.
                      </Label>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <Button type="submit" disabled={status === "submitting"} className="w-full sm:w-auto min-w-[200px] h-11 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-medium tracking-normal transition-transform active:scale-[0.98] shadow-sm">
                        {status === "submitting" ? <span className="flex items-center gap-2">
                            <span className="size-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                            Sending Request...
                          </span> : <span className="flex items-center gap-2">
                            Submit Estimate Request
                            <Send className="size-4" />
                          </span>}
                      </Button>
                    </div>
                  </form>}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}